import React, { useEffect, useState } from "react";
import { Link } from "react-scroll";
import { FiGithub, FiLinkedin, FiArrowDown } from "react-icons/fi";
import { personalInfo } from "../../data/portfolio";
import "./Hero.css";

const roles = ["Full Stack Developer", "MERN Stack Engineer", "Problem Solver"];

function useTypewriter(
  words,
  typingSpeed = 100,
  deletingSpeed = 60,
  pause = 1800,
) {
  const [text, setText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIndex];
    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          setText(current.slice(0, text.length + 1));
          if (text.length + 1 === current.length) {
            setTimeout(() => setIsDeleting(true), pause);
          }
        } else {
          setText(current.slice(0, text.length - 1));
          if (text.length - 1 === 0) {
            setIsDeleting(false);
            setWordIndex((prev) => (prev + 1) % words.length);
          }
        }
      },
      isDeleting ? deletingSpeed : typingSpeed,
    );

    return () => clearTimeout(timeout);
  }, [text, isDeleting, wordIndex, words, typingSpeed, deletingSpeed, pause]);

  return text;
}

function Particles() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: 2 + Math.random() * 3,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 6,
    duration: 4 + Math.random() * 6,
  }));

  return (
    <div className="hero__particles">
      {particles.map((p) => (
        <span
          key={p.id}
          className="hero__particle"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.left}%`,
            top: `${p.top}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

function Hero() {
  const { name, tagline, social } = personalInfo;
  const typedRole = useTypewriter(roles);

  return (
    <section className="hero" id="hero">
      <Particles />

      <div className="hero__container">
        <div className="hero__content">
          <p
            className="hero__greeting anim-fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            Hello, I'm
          </p>
          <h1
            className="hero__name anim-fade-up"
            style={{ animationDelay: "0.4s" }}
          >
            {name}
          </h1>
          <h2
            className="hero__title anim-fade-up"
            style={{ animationDelay: "0.6s" }}
          >
            {typedRole}
            <span className="hero__cursor">|</span>
          </h2>
          <p
            className="hero__tagline anim-fade-up"
            style={{ animationDelay: "0.8s" }}
          >
            {tagline}
          </p>

          <div
            className="hero__cta anim-fade-up"
            style={{ animationDelay: "1s" }}
          >
            <Link
              to="projects"
              smooth
              offset={-80}
              duration={500}
              className="btn btn--primary hero__btn-glow"
            >
              View My Work
            </Link>
            <Link
              to="contact"
              smooth
              offset={-80}
              duration={500}
              className="btn btn--outline"
            >
              Get In Touch
            </Link>
          </div>

          <div
            className="hero__social anim-fade-up"
            style={{ animationDelay: "1.2s" }}
          >
            <a
              href={social.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <FiGithub />
            </a>
            <a
              href={social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <FiLinkedin />
            </a>
          </div>
        </div>

        <div
          className="hero__visual anim-fade-up"
          style={{ animationDelay: "0.6s" }}
        >
          <div className="hero__code-block hero__float">
            <div className="hero__code-header">
              <span className="hero__code-dot hero__code-dot--red" />
              <span className="hero__code-dot hero__code-dot--yellow" />
              <span className="hero__code-dot hero__code-dot--green" />
              <span className="hero__code-filename">developer.js</span>
            </div>
            <pre className="hero__code-content">
              {`const developer = {
  name: "${name}",
  role: "${typedRole}",
  stack: ["MongoDB", "Express",
          "React", "Node.js"],
  passion: "Building things",
  available: true,
};`}
            </pre>
          </div>
        </div>
      </div>

      <Link
        to="about"
        smooth
        offset={-80}
        duration={500}
        className="hero__scroll-indicator anim-fade-up"
        style={{ animationDelay: "1.5s" }}
      >
        <span className="hero__scroll-text">Scroll</span>
        <FiArrowDown />
      </Link>
    </section>
  );
}

export default Hero;
